import React, { useCallback, useContext, useRef, useState } from 'react';
import classNames from 'classnames';
import { ProductItemResponse } from '@/api/product/types';
import { useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	AccountId,
	FilterUnitRegistrationResponse,
	RegistrationCompositeId,
	SparePartRegistrationResponse,
} from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { EditSparePart } from './EditSparePart';
import { RegisterSparePart } from './RegisterSparePart';
import { SparePart } from './SparePart';
import styles from './SparePart.module.scss';

interface SparePartProps {
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	filterUnit: FilterUnitRegistrationResponse;
	productItems?: ProductItemResponse[] | null;
}

/**
 * SparePart component on Application details page
 * SpareParts are a part of FilterUnits
 */

export const SpareParts: React.FunctionComponent<SparePartProps> = ({
	accountId,
	registrationCompositeId,
	filterUnit,
	productItems,
}) => {
	const ariaControlId = 'edit-sparePart';
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);

	const [sparePartToEdit, setSparePartToEdit] = useState<SparePartRegistrationResponse | undefined>(undefined);

	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const openDrawer = useCallback((sparePart: SparePartRegistrationResponse): void => {
		setSparePartToEdit(sparePart);
		handleOpen();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<section className={styles.section}>
			<header
				className={classNames(styles.sectionHeader, { [styles.hasLargeBtn]: !filterUnit.spareParts.length })}
			>
				<h2 className={styles.sectionHeadline}>{translations?.data.applications.sparePartHeadline}</h2>

				<RegisterSparePart
					ariaControlId="register-sparePart"
					accountId={accountId}
					registrationCompositeId={registrationCompositeId}
					filterUnit={filterUnit}
				/>
			</header>

			{filterUnit.spareParts.length > 0 &&
				filterUnit.spareParts.map((sparePart) => (
					<SparePart
						data={sparePart}
						key={sparePart.id}
						translations={translations?.data.sparePartRegistrations}
						productItems={productItems}
						handleClick={openDrawer}
						isOpen={isOpen}
						ariaControlId={ariaControlId}
						cultureCode={settings?.data?.cultureCode ?? ''}
					/>
				))}

			{isRendered && (
				<EditSparePart
					ariaControlId={ariaControlId}
					accountId={accountId}
					registrationCompositeId={registrationCompositeId}
					filterUnitId={filterUnit.id}
					sparePart={sparePartToEdit}
					productItems={productItems}
					isOpen={isOpen}
					drawerRef={drawerRef}
					handleClose={handleClose}
				/>
			)}
		</section>
	);
};
